import { useState } from 'react';
import { Alert, Button, Input, Modal, Select, Space, Table, Tag, Upload } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { UploadOutlined } from '@ant-design/icons';
import { EM_DASH } from '@viq-admin/ui';
// From the leaf `hubspotShared` module, NOT `./HubSpotMappingForm` — that
// file renders `<TemplateImport>`, so importing back from it here would
// recreate the circular import `hubspotShared.tsx` exists to avoid (see its
// header comment). `HubSpotMappingForm.tsx` re-exports both names unchanged.
import { DERIVED_SOURCE_OPTIONS, PropertyPicker } from './hubspotShared';
// `./templateImportSuggestions`, NOT `./templateImport` — see that file's
// header comment (case-insensitive-filesystem collision with THIS file).
// `reshapeForKind` lives there too (not here) even though it's only ever
// CALLED from this component's Kind-select cell: it's pure, deterministic,
// non-trivial branching, and belongs with the rest of the module's
// unit-tested logic rather than in the "presentational" component — see its
// own doc comment there for the full per-branch behavior.
import {
  reshapeForKind,
  suggestFromTemplate,
  type TemplateSuggestion,
} from './templateImportSuggestions';
import type { HubSpotContactProperty, HubSpotDerivedSource } from './types';

export interface TemplateImportProps {
  /** Same list `HubSpotMappingForm` was given — powers the review table's
   * `PropertyPicker` cells exactly like the form's own rows. */
  properties: HubSpotContactProperty[] | null;
  /**
   * Fired ONLY when the operator clicks Apply, with the (possibly
   * hand-edited) reviewed suggestion set. The caller owns turning that into
   * a REPLACE of the form's three sections plus marking it dirty —
   * `TemplateImport` itself never touches the form.
   */
  onApply: (suggestions: TemplateSuggestion[]) => void;
  disabled?: boolean;
}

const KIND_OPTIONS: Array<{ value: TemplateSuggestion['kind']; label: string }> = [
  { value: 'native', label: 'Native (built-in)' },
  { value: 'summary', label: 'AI summary target' },
  { value: 'derived', label: 'Derived binding' },
  { value: 'static', label: 'Static value' },
  { value: 'unmatched', label: 'Skip (unmatched)' },
];

/**
 * "Import from CSV template" — the button, its review Modal, and the wiring
 * between antd `Upload` and the pure `suggestFromTemplate`/`suggestionsToMap`
 * module (Task 9). Deliberately thin: every parsing/matching decision lives
 * in `templateImportSuggestions.ts` (unit-tested there); this component only reads the
 * chosen file, renders the resulting suggestions as an editable review
 * table, and hands the (possibly hand-edited) set to `onApply` — it never
 * touches `HubSpotMappingForm`'s own `Form`/state directly.
 *
 * The uploaded file is parsed ENTIRELY client-side: `beforeUpload` returns
 * `false` (antd never uploads it anywhere) and the text is read via
 * `FileReader`, held only in this component's local state, and discarded —
 * never retained past the parse — the moment it becomes `suggestions`.
 * Closing the modal (Cancel, the X, Escape, or a mask click all route
 * through `handleClose`) clears `suggestions`/`fileName`/`error` too, so
 * nothing survives a dismissed import, reviewed or not.
 */
export function TemplateImport({ properties, onApply, disabled }: TemplateImportProps) {
  const [open, setOpen] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<TemplateSuggestion[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setFileName(null);
    setSuggestions(null);
    setError(null);
  }

  function handleClose() {
    reset();
    setOpen(false);
  }

  function handleFile(file: File): false {
    setError(null);
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : '';
      try {
        setSuggestions(suggestFromTemplate(text, properties));
        setFileName(file.name);
      } catch {
        setError("Couldn't parse this file as a CSV template.");
      }
    };
    reader.onerror = () => setError("Couldn't read this file.");
    reader.readAsText(file);
    return false; // never let antd Upload actually send it anywhere
  }

  function updateRow(index: number, next: TemplateSuggestion) {
    setSuggestions((prev) => (prev ? prev.map((s, i) => (i === index ? next : s)) : prev));
  }

  function handleApply() {
    if (!suggestions) return;
    onApply(suggestions);
    handleClose();
  }

  const columns: ColumnsType<TemplateSuggestion> = [
    {
      title: 'Column',
      key: 'column',
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <span>{row.column}</span>
          <Tag color={row.required ? 'gold' : 'default'}>{row.required ? 'Required' : 'Optional'}</Tag>
        </Space>
      ),
    },
    {
      title: 'Kind',
      key: 'kind',
      render: (_, row, index) => (
        <Select<TemplateSuggestion['kind']>
          aria-label="Kind"
          value={row.kind}
          style={{ minWidth: 170 }}
          options={KIND_OPTIONS}
          onChange={(kind) => updateRow(index, reshapeForKind(row, kind))}
        />
      ),
    },
    {
      title: 'Property',
      key: 'property',
      render: (_, row, index) => {
        if (row.kind === 'native' || row.kind === 'unmatched') return <span>{EM_DASH}</span>;
        return (
          <PropertyPicker
            properties={properties}
            ariaLabel="Property"
            allowClear
            placeholder="Choose a property…"
            value={row.property ?? undefined}
            onChange={(value) => updateRow(index, { ...row, property: value ?? null })}
          />
        );
      },
    },
    {
      title: 'Value / Source',
      key: 'valueOrSource',
      render: (_, row, index) => {
        if (row.kind === 'static') {
          return (
            <Input
              aria-label="Value"
              value={row.value}
              onChange={(e) => updateRow(index, { ...row, value: e.target.value })}
            />
          );
        }
        if (row.kind === 'derived') {
          return (
            <Select<HubSpotDerivedSource>
              aria-label="Source"
              value={row.source}
              style={{ minWidth: 220 }}
              options={DERIVED_SOURCE_OPTIONS}
              onChange={(source) => updateRow(index, { ...row, source })}
            />
          );
        }
        if (row.kind === 'unmatched') return <span>{row.example || EM_DASH}</span>;
        return <span>{EM_DASH}</span>;
      },
    },
  ];

  return (
    <>
      <Button onClick={() => setOpen(true)} disabled={disabled}>
        Import from CSV template
      </Button>
      <Modal
        title="Import mapping from CSV template"
        open={open}
        onCancel={handleClose}
        width={860}
        // Without this, antd keeps the closed Modal's last content mounted
        // (hidden via CSS, not removed) — harmless visually, but it leaves
        // the review table's rows (and whatever they matched, e.g. a
        // property also shown elsewhere in the live form) findable by text
        // queries after Cancel, and is one more reason a "parsed in memory,
        // discarded on close" file's data shouldn't still be sitting in the
        // DOM. Forces a real unmount instead of a hide.
        destroyOnHidden
        footer={[
          <Button key="cancel" onClick={handleClose}>
            Cancel
          </Button>,
          <Button
            key="apply"
            type="primary"
            onClick={handleApply}
            disabled={!suggestions || suggestions.length === 0}
          >
            Apply
          </Button>,
        ]}
      >
        <p>
          Template shape: 3 header rows — guidance (ignored), Required / Optional, then column names — followed
          by example rows. The file is read in your browser only; it is never uploaded.
        </p>
        {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}
        {!suggestions && (
          <Upload accept=".csv" showUploadList={false} beforeUpload={handleFile}>
            <Button icon={<UploadOutlined />}>Choose CSV file…</Button>
          </Upload>
        )}
        {suggestions && (
          <>
            <Space style={{ marginBottom: 12 }}>
              <span>{fileName}</span>
              <Button size="small" onClick={reset}>
                Choose a different file
              </Button>
            </Space>
            <Table<TemplateSuggestion>
              size="small"
              pagination={false}
              rowKey={(_, index) => String(index ?? 0)}
              dataSource={suggestions}
              columns={columns}
              onRow={(_, index) => ({ 'data-testid': `template-import-row-${index}` }) as React.HTMLAttributes<HTMLElement>}
            />
          </>
        )}
      </Modal>
    </>
  );
}
